import { useCallback, useEffect, useMemo, useState } from 'react';

const API_BASE = '/api/v1/maintenance';

function MaintReports({ token, selectedProperty }) {
  const headers = useMemo(() => ({
    Authorization: 'Bearer ' + token,
    'x-chain-id': selectedProperty?.chain_id,
    'x-property-id': selectedProperty?.id
  }), [token, selectedProperty]);

  const [activeTab, setActiveTab] = useState('summary');

  const tabs = [
    { key: 'summary', label: 'Summary' },
    { key: 'technician', label: 'Technician Performance' },
    { key: 'pm', label: 'PM Compliance' },
    { key: 'spare', label: 'Spare Consumption' },
    { key: 'sla', label: 'SLA Breach' },
    { key: 'cost', label: 'Cost Analysis' }
  ];

  return (
    <div className="page-shell">
      <h2 style={{ color: '#1f8f4d', marginBottom: '0.75rem' }}>Maintenance Reports</h2>
      <div style={{ display: 'flex', gap: '0.45rem', flexWrap: 'wrap', borderBottom: '1px solid #dcdde1', paddingBottom: '0.55rem', marginBottom: '0.8rem' }}>
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              padding: '0.42rem 0.8rem',
              borderRadius: '6px',
              border: '1px solid #dcdde1',
              cursor: 'pointer',
              background: tab.key === activeTab ? 'var(--theme-accent)' : '#fff',
              color: tab.key === activeTab ? '#fff' : '#1f8f4d',
              fontWeight: 700
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>
      <div>
        {activeTab === 'summary' && <SummaryTab headers={headers} />}
        {activeTab === 'technician' && <TechnicianTab headers={headers} />}
        {activeTab === 'pm' && <PmComplianceTab headers={headers} />}
        {activeTab === 'spare' && <SpareConsumptionTab headers={headers} />}
        {activeTab === 'sla' && <SlaBreachTab headers={headers} />}
        {activeTab === 'cost' && <CostAnalysisTab headers={headers} />}
      </div>
    </div>
  );
}

/* ──────────── shared helpers ──────────── */

const tableStyle = { width: '100%', borderCollapse: 'collapse', fontSize: '0.92rem' };
const thStyle = { textAlign: 'left', padding: '0.5rem 0.6rem', borderBottom: '2px solid #dcdde1', background: '#f8f9fa', fontWeight: 700, color: '#333' };
const tdStyle = { padding: '0.5rem 0.6rem', borderBottom: '1px solid #eee' };
const cardStyle = { background: '#fff', border: '1px solid #dcdde1', borderRadius: '8px', padding: '1rem', marginBottom: '1rem' };
const loadingStyle = { padding: '2rem', textAlign: 'center', color: '#888' };
const errorStyle = { padding: '1rem', color: '#c0392b', background: '#fdecea', borderRadius: '6px', marginBottom: '0.75rem' };

function useFetchReport(url, headers) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchData = useCallback(async (queryParams) => {
    setLoading(true);
    setError('');
    try {
      let fullUrl = API_BASE + url;
      if (queryParams) {
        const qs = new URLSearchParams(queryParams).toString();
        if (qs) fullUrl += '?' + qs;
      }
      const res = await fetch(fullUrl, { headers });
      if (!res.ok) throw new Error('Server returned ' + res.status);
      const json = await res.json();
      setData(json);
    } catch (err) {
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [url, headers]);

  return { data, loading, error, fetchData };
}

function fmt(v) {
  if (v == null) return '—';
  if (typeof v === 'number') return v.toLocaleString();
  return String(v);
}

/* ──────────── Summary ──────────── */

function SummaryTab({ headers }) {
  const today = new Date().toISOString().slice(0, 10);
  const thirtyDaysAgo = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);
  const [fromDate, setFromDate] = useState(thirtyDaysAgo);
  const [toDate, setToDate] = useState(today);
  const { data, loading, error, fetchData } = useFetchReport('/reports/summary', headers);

  useEffect(() => {
    fetchData({ from_date: fromDate, to_date: toDate });
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const handleApply = () => fetchData({ from_date: fromDate, to_date: toDate });

  return (
    <div>
      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '1rem' }}>
        <label style={{ fontWeight: 600 }}>From
          <input type="date" value={fromDate} onChange={e => { const v = e.target.value; setFromDate(v); if (toDate && v && toDate < v) setToDate(v); }} style={{ marginLeft: '0.4rem', padding: '0.3rem 0.5rem', border: '1px solid #ccc', borderRadius: '4px' }} />
        </label>
        <label style={{ fontWeight: 600 }}>To
          <input type="date" value={toDate} min={fromDate || undefined} onChange={e => { const v = e.target.value; setToDate(v); if (fromDate && v && fromDate > v) setFromDate(v); }} style={{ marginLeft: '0.4rem', padding: '0.3rem 0.5rem', border: '1px solid #ccc', borderRadius: '4px' }} />
        </label>
        <button onClick={handleApply} style={{ padding: '0.38rem 1rem', background: 'var(--theme-accent)', color: '#fff', border: 'none', borderRadius: '6px', fontWeight: 700, cursor: 'pointer' }}>Apply</button>
      </div>

      {error && <div style={errorStyle}>{error}</div>}
      {loading && <div style={loadingStyle}>Loading...</div>}

      {data && !loading && (
        <>
          {/* Totals */}
          {data.totals && (
            <div style={{ ...cardStyle, display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
              {Object.entries(data.totals).map(([k, v]) => (
                <div key={k} style={{ textAlign: 'center', minWidth: '120px' }}>
                  <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#1f8f4d' }}>{fmt(v)}</div>
                  <div style={{ fontSize: '0.82rem', color: '#666', textTransform: 'capitalize' }}>{k.replace(/_/g, ' ')}</div>
                </div>
              ))}
            </div>
          )}

          {/* By Category */}
          {data.by_category && data.by_category.length > 0 && (
            <div style={cardStyle}>
              <h4 style={{ margin: '0 0 0.5rem', color: '#333' }}>By Category</h4>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    {Object.keys(data.by_category[0]).map(col => (
                      <th key={col} style={thStyle}>{col.replace(/_/g, ' ')}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.by_category.map((row, i) => (
                    <tr key={i}>
                      {Object.values(row).map((val, j) => <td key={j} style={tdStyle}>{fmt(val)}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* By Priority */}
          {data.by_priority && data.by_priority.length > 0 && (
            <div style={cardStyle}>
              <h4 style={{ margin: '0 0 0.5rem', color: '#333' }}>By Priority</h4>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    {Object.keys(data.by_priority[0]).map(col => (
                      <th key={col} style={thStyle}>{col.replace(/_/g, ' ')}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.by_priority.map((row, i) => (
                    <tr key={i}>
                      {Object.values(row).map((val, j) => <td key={j} style={tdStyle}>{fmt(val)}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ──────────── Technician Performance ──────────── */

function TechnicianTab({ headers }) {
  const { data, loading, error, fetchData } = useFetchReport('/reports/technician-performance', headers);

  useEffect(() => { fetchData(); }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const columns = ['staff_name', 'total_assigned', 'completed', 'active', 'avg_resolution_hours', 'sla_breaches'];

  return (
    <div>
      {error && <div style={errorStyle}>{error}</div>}
      {loading && <div style={loadingStyle}>Loading...</div>}
      {data && !loading && Array.isArray(data) && (
        <div style={cardStyle}>
          <table style={tableStyle}>
            <thead>
              <tr>
                {columns.map(col => <th key={col} style={thStyle}>{col.replace(/_/g, ' ')}</th>)}
              </tr>
            </thead>
            <tbody>
              {data.map((row, i) => (
                <tr key={i}>
                  {columns.map(col => (
                    <td key={col} style={tdStyle}>
                      {col === 'avg_resolution_hours' ? (row[col] != null ? Number(row[col]).toFixed(1) : '—') : fmt(row[col])}
                    </td>
                  ))}
                </tr>
              ))}
              {data.length === 0 && (
                <tr><td colSpan={columns.length} style={{ ...tdStyle, textAlign: 'center', color: '#888' }}>No data</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ──────────── PM Compliance ──────────── */

function complianceColor(pct) {
  if (pct == null) return '#888';
  if (pct < 80) return '#c0392b';
  if (pct <= 95) return '#e67e22';
  return '#27ae60';
}

function PmComplianceTab({ headers }) {
  const { data, loading, error, fetchData } = useFetchReport('/reports/pm-compliance', headers);

  useEffect(() => { fetchData(); }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const columns = ['schedule_name', 'frequency_type', 'total_generated', 'completed', 'compliance_pct'];

  return (
    <div>
      {error && <div style={errorStyle}>{error}</div>}
      {loading && <div style={loadingStyle}>Loading...</div>}
      {data && !loading && Array.isArray(data) && (
        <div style={cardStyle}>
          <table style={tableStyle}>
            <thead>
              <tr>
                {columns.map(col => <th key={col} style={thStyle}>{col.replace(/_/g, ' ')}</th>)}
              </tr>
            </thead>
            <tbody>
              {data.map((row, i) => (
                <tr key={i}>
                  {columns.map(col => (
                    <td key={col} style={{
                      ...tdStyle,
                      ...(col === 'compliance_pct' ? { fontWeight: 700, color: complianceColor(row[col]) } : {})
                    }}>
                      {col === 'compliance_pct' ? (row[col] != null ? Number(row[col]).toFixed(1) + '%' : '—') : fmt(row[col])}
                    </td>
                  ))}
                </tr>
              ))}
              {data.length === 0 && (
                <tr><td colSpan={columns.length} style={{ ...tdStyle, textAlign: 'center', color: '#888' }}>No data</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ──────────── Spare Consumption ──────────── */

function SpareConsumptionTab({ headers }) {
  const { data, loading, error, fetchData } = useFetchReport('/reports/spare-consumption', headers);

  useEffect(() => { fetchData(); }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const columns = ['part_code', 'part_name', 'total_used', 'total_cost', 'current_stock'];

  return (
    <div>
      {error && <div style={errorStyle}>{error}</div>}
      {loading && <div style={loadingStyle}>Loading...</div>}
      {data && !loading && Array.isArray(data) && (
        <div style={cardStyle}>
          <table style={tableStyle}>
            <thead>
              <tr>
                {columns.map(col => <th key={col} style={thStyle}>{col.replace(/_/g, ' ')}</th>)}
              </tr>
            </thead>
            <tbody>
              {data.map((row, i) => (
                <tr key={i}>
                  {columns.map(col => (
                    <td key={col} style={tdStyle}>
                      {col === 'total_cost' ? (row[col] != null ? Number(row[col]).toFixed(2) : '—') : fmt(row[col])}
                    </td>
                  ))}
                </tr>
              ))}
              {data.length === 0 && (
                <tr><td colSpan={columns.length} style={{ ...tdStyle, textAlign: 'center', color: '#888' }}>No data</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ──────────── SLA Breach ──────────── */

function SlaBreachTab({ headers }) {
  const { data, loading, error, fetchData } = useFetchReport('/reports/sla-breach', headers);

  useEffect(() => { fetchData(); }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      {error && <div style={errorStyle}>{error}</div>}
      {loading && <div style={loadingStyle}>Loading...</div>}
      {data && !loading && Array.isArray(data) && (
        <div style={cardStyle}>
          {data.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#888', padding: '2rem' }}>No SLA breaches found</div>
          ) : (
            <table style={tableStyle}>
              <thead>
                <tr>
                  {Object.keys(data[0]).map(col => <th key={col} style={thStyle}>{col.replace(/_/g, ' ')}</th>)}
                </tr>
              </thead>
              <tbody>
                {data.map((row, i) => (
                  <tr key={i}>
                    {Object.values(row).map((val, j) => <td key={j} style={tdStyle}>{fmt(val)}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

/* ──────────── Cost Analysis ──────────── */

function CostAnalysisTab({ headers }) {
  const { data, loading, error, fetchData } = useFetchReport('/reports/cost-analysis', headers);

  useEffect(() => { fetchData(); }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      {error && <div style={errorStyle}>{error}</div>}
      {loading && <div style={loadingStyle}>Loading...</div>}
      {data && !loading && (
        <>
          {data.by_category && data.by_category.length > 0 && (
            <div style={cardStyle}>
              <h4 style={{ margin: '0 0 0.5rem', color: '#333' }}>Cost by Category</h4>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    {Object.keys(data.by_category[0]).map(col => <th key={col} style={thStyle}>{col.replace(/_/g, ' ')}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {data.by_category.map((row, i) => (
                    <tr key={i}>
                      {Object.values(row).map((val, j) => <td key={j} style={tdStyle}>{fmt(val)}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {data.by_room && data.by_room.length > 0 && (
            <div style={cardStyle}>
              <h4 style={{ margin: '0 0 0.5rem', color: '#333' }}>Cost by Room</h4>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    {Object.keys(data.by_room[0]).map(col => <th key={col} style={thStyle}>{col.replace(/_/g, ' ')}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {data.by_room.map((row, i) => (
                    <tr key={i}>
                      {Object.values(row).map((val, j) => <td key={j} style={tdStyle}>{fmt(val)}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {(!data.by_category || data.by_category.length === 0) && (!data.by_room || data.by_room.length === 0) && (
            <div style={{ ...cardStyle, textAlign: 'center', color: '#888', padding: '2rem' }}>No cost data available</div>
          )}
        </>
      )}
    </div>
  );
}

export default MaintReports;
